export const INGEST_REJECTION_CODES = [
  'bad_layer_name',
  'unknown_layer_role',
  'duplicate_layer_role',
  'missing_base_layer',
  'mixed_option_base',
  'unreadable_image',
  'bad_dimensions',
  'blank_layer',
  'layer_size_mismatch',
  'upload_not_found',
  'unknown_set',
  'duplicate_option',
] as const

export const PUBLISH_GATE_CODES = [
  'append_only_violation',
  'hard_delete_rejected',
  'incomplete_layers',
  'manifest_mismatch',
  'missing_object',
  'empty_manifest',
  'approval_required',
] as const

export const REJECTION_CODES = [
  ...INGEST_REJECTION_CODES,
  ...PUBLISH_GATE_CODES,
] as const

export type RejectionCode = (typeof REJECTION_CODES)[number]

/**
 * Code -> the message id that says what to DO about it. Mirrors avatar-platform's
 * `IngestRejectionCode`; the test asserts none is missing, so a code added there cannot reach an
 * admin as a raw string.
 */
export const REJECTION_MESSAGE_IDS: Readonly<Record<RejectionCode, string>> = {
  bad_layer_name: 'avatarStudio.reject.badLayerName',
  unknown_layer_role: 'avatarStudio.reject.unknownLayerRole',
  duplicate_layer_role: 'avatarStudio.reject.duplicateLayerRole',
  missing_base_layer: 'avatarStudio.reject.missingBaseLayer',
  mixed_option_base: 'avatarStudio.reject.mixedOptionBase',
  unreadable_image: 'avatarStudio.reject.unreadableImage',
  bad_dimensions: 'avatarStudio.reject.badDimensions',
  blank_layer: 'avatarStudio.reject.blankLayer',
  layer_size_mismatch: 'avatarStudio.reject.layerSizeMismatch',
  upload_not_found: 'avatarStudio.reject.uploadNotFound',
  unknown_set: 'avatarStudio.reject.unknownSet',
  duplicate_option: 'avatarStudio.reject.duplicateOption',
  append_only_violation: 'avatarStudio.reject.appendOnlyViolation',
  hard_delete_rejected: 'avatarStudio.reject.hardDeleteRejected',
  incomplete_layers: 'avatarStudio.reject.incompleteLayers',
  manifest_mismatch: 'avatarStudio.reject.manifestMismatch',
  missing_object: 'avatarStudio.reject.missingObject',
  empty_manifest: 'avatarStudio.reject.emptyManifest',
  approval_required: 'avatarStudio.reject.approvalRequired',
}

export const UNKNOWN_REJECTION_MESSAGE_ID = 'avatarStudio.reject.unknown'

export function messageIdForCode(code: string): string {
  return (
    REJECTION_MESSAGE_IDS[code as RejectionCode] ?? UNKNOWN_REJECTION_MESSAGE_ID
  )
}

export interface FileIssue {
  file?: string
  code: string
  detail?: string
}

/** Issues grouped by the file they name, so each lands on its own row. */
export function issuesByFile(
  issues: FileIssue[],
): { byFile: Map<string, FileIssue[]>; general: FileIssue[] } {
  const byFile = new Map<string, FileIssue[]>()
  const general: FileIssue[] = []

  issues.forEach((issue) => {
    const named = fileFromIssue(issue)
    if (!named) {
      general.push(issue)

      return
    }
    byFile.set(named, [...(byFile.get(named) ?? []), issue])
  })

  return { byFile, general }
}

/**
 * The API puts the filename in `file`, and the controller ALSO prefixes `detail` with
 * `"<file>: "` when flattening. Either shape has to land on the right row.
 */
export function fileFromIssue(issue: FileIssue): string | undefined {
  if (issue.file) {
    return issue.file
  }
  const prefixed = /^([^\s:]+\.[A-Za-z0-9]+):\s/.exec(issue.detail ?? '')

  return prefixed?.[1]
}
